import clsx from "clsx";
import { FocusEvent } from "react";
import { Link, useLocation } from "react-router";
import { FCC } from "types";

import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

type TabWithDisableHintProps = {
  target: string;
  disabled: boolean;
  /** Single sentence explaining why the tab cannot be opened right now. */
  disabledHint?: string;
};

export const tabClassName =
  "group relative flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-body-base !font-semibold leading-none outline-none";

const scrollTabIntoView = (event: FocusEvent<HTMLElement>) => {
  // jsdom (unit tests) has no scrollIntoView — guard for that.
  event.currentTarget.scrollIntoView?.({
    block: "nearest",
    inline: "nearest",
  });
};

export const TabWithDisableHint: FCC<TabWithDisableHintProps> = ({
  target,
  disabled,
  disabledHint,
  children,
}) => {
  const { pathname } = useLocation();
  // Targets are relative and carry the SIM in a query ("voice?simId=1"), so
  // the selected tab is read off the leading path segment. This one value
  // drives both the highlight and aria-current, for enabled and disabled tabs.
  const isSelected = pathname
    .split("/")
    .includes(target.split("?")[0].replace(/^\//, ""));

  if (disabled) {
    const tab = (
      <span
        className={clsx(
          tabClassName,
          "cursor-not-allowed text-foreground-tertiary",
        )}
        role="link"
        aria-disabled="true"
        aria-current={isSelected ? "page" : undefined}
        // Focusable without being followable, so keyboard users reach the tab
        // and hear why it is closed instead of skipping over it.
        // Explicit 0: Safari otherwise skips the control in the Tab cycle.
        tabIndex={disabledHint ? KEYBOARD_TAB_INDEX : undefined}
        onFocus={scrollTabIntoView}
      >
        <FocusRing inside />
        {/* Optical baseline centering for Gellix, as on the design system button. */}
        <span className="pb-1">{children}</span>
      </span>
    );

    return disabledHint ? (
      <HintTooltip content={disabledHint}>{tab}</HintTooltip>
    ) : (
      tab
    );
  }

  return (
    <Link
      className={clsx(
        tabClassName,
        "text-foreground-primary transition-colors hover:bg-surface-secondary active:bg-surface-secondary",
        isSelected && "bg-surface-tertiary",
      )}
      to={target}
      replace
      tabIndex={KEYBOARD_TAB_INDEX}
      aria-current={isSelected ? "page" : undefined}
      onFocus={scrollTabIntoView}
    >
      <FocusRing inside />
      <span className="pb-1">{children}</span>
    </Link>
  );
};
